import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronRight, Search, Send, FileText, CheckCircle, DollarSign, Target, Handshake, Eye, BookOpen, Zap, Video, PenTool, Sparkles } from 'lucide-react';
import LaunchCampaignChoiceModal from '../components/LaunchCampaignChoiceModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function AnimatedCounter({ end, prefix = '', suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration]);
  return <span className="font-mono text-2xl sm:text-4xl font-bold text-[#FF4343]">{prefix}{count.toLocaleString()}{suffix}</span>;
}

export default function ForBrands() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => { fetchCaseStudies(); }, []);

  const fetchCaseStudies = async () => {
    try {
      const { data } = await axios.get(`${API}/case-studies?featured=true`);
      setCaseStudies(data.slice(0, 3));
    } catch { /* ignore — non-critical section */ }
  };

  const verticals = [
    { key: 'automotive', label: 'Automotive' },
    { key: 'medical', label: 'Medical' },
    { key: 'realestate', label: 'Real Estate' },
    { key: 'tech', label: 'Tech' },
    { key: 'finance', label: 'Finance' },
    { key: 'legal', label: 'Legal' },
    { key: 'retail', label: 'Retail' },
    { key: 'manufacturing', label: 'Manufacturing' },
    { key: 'energy', label: 'Energy' },
    { key: 'logistics', label: 'Logistics' },
    { key: 'agriculture', label: 'Agriculture' },
    { key: 'foodhospitality', label: 'Food & Hospitality' },
    { key: 'marketingmedia', label: 'Marketing/Media' },
  ];

  // The four services — the same taxonomy the brand portal uses.
  const offerings = [
    {
      key: 'endorse',
      tag: 'Endorse',
      icon: Handshake,
      title: 'Executive Endorsements',
      one_liner: 'The core offering. Verified industry practitioners publish paid endorsements to their real audiences.',
      details: 'We source dealer principals, practice owners, managing brokers, CTOs, and other operators your buyers already trust. They draft the post, we bring it back to you for revisions, then it publishes on LinkedIn, X, newsletters, podcasts, trade publications, and in-person events. Every deliverable is co-branded with a tracked landing page.',
      chips: ['LinkedIn', 'X / Twitter', 'Podcasts', 'Newsletters', 'Trade press', 'Speaking'],
    },
    {
      key: 'amplify',
      tag: 'Amplify',
      icon: Zap,
      title: 'Amplify — Paid Ads on Endorsement Content',
      one_liner: 'Turn your best-performing endorsement posts into paid ads across LinkedIn, Meta, and X.',
      details: 'Once an executive post lands, we re-deploy it as a whitelisted paid ad from their handle — the highest-trust ad format that exists in B2B. Our media buyers manage targeting, creative iteration, and reporting. Pricing is $500 flat management + 15% of ad spend, and we operate on your ad budget with full transparency.',
      chips: ['LinkedIn Ads', 'Meta Ads', 'X Ads', 'Whitelisting', 'Retargeting', 'CTV / OTT'],
    },
    {
      key: 'produce',
      tag: 'Produce',
      icon: Video,
      title: 'Produce — Video, Clipping & UGC',
      one_liner: 'On-site shoots, remote interviews, podcast clip factories, editing, and UGC production.',
      details: 'Book our production crew for on-location video shoots, remote video interviews, or a monthly clipping retainer that turns your long-form content (webinars, keynotes, podcasts) into short-form assets ready for LinkedIn, YouTube Shorts, Reels, and TikTok. We handle scripting, filming, editing, subtitling, and delivery.',
      chips: ['On-site video', 'Podcast clipping', 'Reels / Shorts', 'UGC batches', 'Editing retainers', 'Subtitles'],
    },
    {
      key: 'craft',
      tag: 'Craft',
      icon: PenTool,
      title: 'Craft — Writing Services',
      one_liner: 'Blog articles, ghost-written LinkedIn posts, newsletter copy, sales collateral, and ebooks.',
      details: 'Our writing team ghostwrites long-form blog content, LinkedIn post cadences for executives, newsletter issues, sales one-pagers, whitepapers, and case-study write-ups. Every piece runs through your brand voice guide and is delivered ready to publish.',
      chips: ['Blog articles', 'LinkedIn cadence', 'Newsletters', 'Sales copy', 'Case studies', 'Ebooks / Whitepapers'],
    },
  ];

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="for-brands-page">
      {/* Hero */}
      <section className="relative py-16 sm:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#090E33] via-[#090E33] to-[#111827]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#FF4343]/5 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6 animate-fade-in-up">
              <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="Freddy Media" className="h-10 w-10" />
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF4343]">
                B2B Executive Endorsement Agency
              </span>
            </div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-7xl font-bold leading-none mb-6 sm:mb-8 animate-fade-in-up stagger-1" data-testid="brands-hero-heading">
              Where Industry<br />
              Authority <span className="text-[#FF4343]">Converts</span>.
            </h1>
            <p className="text-base sm:text-lg text-[#9CA3AF] max-w-2xl mb-8 sm:mb-10 leading-relaxed animate-fade-in-up stagger-2">
              We source verified industry executives who publish paid endorsements — then deploy those posts as paid ads, cut them into short-form video, and ghostwrite the copy that surrounds them. One agency, one campaign, one team of humans running it for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <button onClick={() => setFormOpen(true)} data-testid="request-proposal-btn"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] hover:border-[#E03030] px-8 py-3.5 text-sm font-semibold transition-all duration-300">
                Request a Campaign Proposal <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/login?as=executive&tab=register" data-testid="apply-exec-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/20 bg-transparent text-white hover:bg-white/5 px-8 py-3.5 text-sm font-medium transition-all duration-200">
                Apply to Join Our Executive Roster
              </Link>
            </div>
            <a href="#how-it-works" data-testid="scroll-to-how"
              className="mt-12 inline-flex flex-col items-center gap-2 text-[#9CA3AF] hover:text-white transition-colors animate-fade-in-up stagger-3 group">
              <span className="text-xs font-mono uppercase tracking-[0.2em]">See How It Works</span>
              <svg className="w-5 h-5 animate-bounce group-hover:text-[#FF4343]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Offerings overview */}
      <section id="offerings" className="py-16 sm:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <Sparkles className="w-4 h-4 text-[#FF4343]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF4343]">The Freddy Media Stack</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-4">Four Services. <span className="text-[#FF4343]">One Team.</span></h2>
          <p className="text-sm text-[#9CA3AF] text-center max-w-2xl mx-auto mb-12 sm:mb-16">Book one, book all four. Every engagement runs through a dedicated Freddy Media Campaign Manager — no self-serve dashboards to babysit.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-testid="offerings-grid">
            {offerings.map((o) => (
              <div key={o.key} data-testid={`offering-${o.key}`}
                className="bg-[#111827] border border-white/10 hover:border-[#FF4343]/40 p-5 sm:p-7 transition-all duration-200 group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#FF4343]/10 border border-[#FF4343]/30 flex items-center justify-center flex-shrink-0">
                    <o.icon className="w-5 h-5 text-[#FF4343]" />
                  </div>
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#FF4343]">{o.tag}</p>
                    <h3 className="font-display text-base sm:text-lg font-semibold text-white leading-tight">{o.title}</h3>
                  </div>
                </div>
                <p className="text-sm text-white/90 mb-2 leading-relaxed">{o.one_liner}</p>
                <p className="text-xs text-[#9CA3AF] mb-4 leading-relaxed">{o.details}</p>
                <div className="flex flex-wrap gap-1.5">
                  {o.chips.map(c => (
                    <span key={c} className="text-[10px] font-mono uppercase text-[#9CA3AF] bg-white/5 border border-white/10 px-2 py-0.5">{c}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button onClick={() => setFormOpen(true)} data-testid="offerings-cta"
              className="inline-flex items-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-6 py-3 text-sm font-semibold transition-all">
              Get a Multi-Layer Proposal <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[10px] text-[#6B7280] font-mono mt-3">Mix and match — a single Statement of Work covers all four.</p>
          </div>
        </div>
      </section>

      {/* How It Works — unified process */}
      <section id="how-it-works" className="py-16 sm:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-4">How It <span className="text-[#FF4343]">Works</span></h2>
          <p className="text-sm text-[#9CA3AF] text-center max-w-2xl mx-auto mb-12 sm:mb-16">From kickoff call to verified performance — the entire process runs through your dedicated Freddy Media Campaign Manager.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: Handshake, num: '01', title: 'Kickoff & Discovery', desc: 'Book a 30-min working session with your Campaign Manager. We scope the mix — endorsements, ads, video, writing — and lock the budget together.' },
              { icon: FileText, num: '02', title: 'You Get a Custom Proposal', desc: 'Your CM builds a multi-layer Statement of Work: line-itemed by deliverable, service, and channel. Sign it virtually, we handle the rest.' },
              { icon: Search, num: '03', title: 'We Source the Executives', desc: 'From our roster of verified operators, we hand-pick the executives your buyers already trust. You approve every voice before they get an offer.' },
              { icon: Eye, num: '04', title: 'Content Gets Made', desc: 'Executives draft posts. Our writing team ghostwrites the surrounding copy. Our production crew shoots video and clips. You review everything before it publishes.' },
              { icon: Zap, num: '05', title: 'Publish + Amplify with Paid Ads', desc: 'Approved endorsement posts go live on LinkedIn, X, newsletters and podcasts — then our media team turns the winners into paid ads, whitelisted from the executive\'s handle for maximum trust.' },
              { icon: DollarSign, num: '06', title: 'Verify, Report, Iterate', desc: 'AI verification confirms every post. Your CM sends weekly performance reports with impressions, engagement, and CPM — plus recommendations for the next round.' },
            ].map((step, i) => (
              <div key={i} className="bg-[#111827] border border-white/10 p-5 sm:p-7 hover:border-white/20 transition-colors group animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-3xl font-bold text-[#FF4343]/20">{step.num}</span>
                  <step.icon className="w-5 h-5 text-[#FF4343]" />
                </div>
                <h3 className="font-display text-base sm:text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-[#9CA3AF] text-xs sm:text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Dive Article */}
      <section className="py-16 sm:py-20 bg-[#111827]/30 border-y border-white/5" id="deep-dive">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <div className="flex items-center gap-2 mb-6">
            <BookOpen className="w-5 h-5 text-[#FF4343]" />
            <span className="text-xs font-mono uppercase tracking-wider text-[#FF4343]">The Full Guide</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-8">How the Freddy Media Machine <span className="text-[#FF4343]">Works</span></h2>

          <div className="prose prose-invert max-w-none space-y-8">
            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">What is an executive endorsement?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Unlike influencer marketing, executive endorsements come from verified industry practitioners — the dealer principals, practice owners, managing brokers, and CTOs that your buyers already trust. When they recommend a tool or service, their peers listen because they know it&apos;s coming from someone who actually uses it.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">How do you find the right executives?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Every engagement starts with a call between you and your Freddy Media Campaign Manager. We map your ideal customer profile, pull matching executives from our verified roster, and share the shortlist for your approval. For high-value targets (&ldquo;whales&rdquo;), we build <strong className="text-white">bespoke offers</strong> — a flat fee, CPM rate, and bonus tailored to their audience reach. You never have to search a database or write an outreach message.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">Then what — how does the content get made?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Accepted executives enter a <strong className="text-white">Campaign Workspace</strong> — a private deal room with your brand guidelines, talking points, guardrails, and example posts. Your Campaign Manager coaches them through the draft, our writing team polishes it if needed, and the piece comes back to you inside your Brand Portal for review. Nothing publishes until you say so.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">Where do endorsements get published?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Everywhere executive voices live — <strong className="text-white">LinkedIn, X (Twitter), newsletters, podcasts, trade publications, and in-person events</strong>. Each campaign defines which platforms are in scope. Executives post to their real, established audiences — not bought followers, not bots. Every post includes a co-branded landing page for attribution.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">What is <span className="text-[#FF4343]">Amplify</span>, and why should I care?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Amplify is where we take the endorsement posts that are already performing organically and turn them into <strong className="text-white">paid ads whitelisted from the executive&apos;s own handle</strong>. That means when a prospect sees the ad, it looks like an authentic post from a trusted operator — not a corporate promo. It&apos;s the highest-converting ad format in B2B. Our media team runs the buys across LinkedIn Ads, Meta Ads, X Ads, and retargeting funnels. Pricing is a flat <strong className="text-white">$500 management fee plus 15% of ad spend</strong>. You control the budget, we optimize the creative and targeting weekly.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">What if I need <span className="text-[#FF4343]">video content</span> too?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                That&apos;s <strong className="text-white">Produce</strong>. Book our production crew for on-location shoots, remote video interviews with executives, or a monthly clipping retainer that mines your long-form content — webinars, keynotes, podcast episodes — for LinkedIn-native shorts, YouTube Shorts, Reels, and TikToks. We handle scripting, filming, editing, subtitling, and delivery. Pair it with Amplify and your endorsement videos become high-performing paid ads within weeks.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">Do you also handle <span className="text-[#FF4343]">writing</span>?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Yes — that&apos;s <strong className="text-white">Craft</strong>. Our writing bench ghostwrites the copy that surrounds your executive endorsements: long-form blog articles, LinkedIn post cadences (for executives who need help staying in-market between endorsements), newsletter issues, sales one-pagers, whitepapers, and case-study write-ups. Every piece runs through your brand voice guide and is delivered ready to publish or hand off to your team.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">How is performance verified?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                After publishing, executives submit their live post URL and a screenshot. Our <strong className="text-white">AI verification system</strong> checks the screenshot against the URL using Vision AI, validates the post exists via platform APIs (LinkedIn, X, YouTube), detects anomalies in engagement, and flags any content that doesn&apos;t match your approved draft. Your CM reviews the report and pushes it into your weekly performance summary — real-time impressions, engagement rate, and CPM on the endorsement side; CPA, ROAS, and creative rank on the Amplify side.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">How does payment work?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Every engagement lives inside a signed Statement of Work with line-itemed deliverables. Endorsements are paid on <strong className="text-white">flat fees</strong>, <strong className="text-white">CPM</strong>, <strong className="text-white">hybrid</strong>, or <strong className="text-white">performance bonuses</strong>. Amplify runs on your ad-budget commitment plus our flat management + 15% spend fee. Produce and Craft are project-based or on a monthly retainer. All charges route through Stripe with a single card on file — no scattered invoices from a dozen vendors.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">What does it cost to get started?</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                There&apos;s no platform fee or subscription. Every proposal is custom-scoped on the discovery call — from a single-executive endorsement test to a flagship multi-channel program blending Endorse + Amplify + Produce + Craft. We&apos;ll quote a total you&apos;re comfortable with, then invoice inside a signed SOW. No retainers required unless you want ongoing Amplify or Craft support.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-10 pt-8 border-t border-white/10">
            <button onClick={() => setFormOpen(true)} data-testid="article-proposal-btn"
              className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-3 text-sm font-semibold hover:bg-[#E03030] transition-all text-center">
              Request a Proposal
            </button>
            <button onClick={() => setFormOpen(true)} data-testid="article-book-btn"
              className="flex-1 flex items-center justify-center gap-2 border border-white/20 text-white py-3 text-sm font-medium hover:bg-white/5 transition-all">
              Book a Discovery Call <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 sm:py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-12">Why <span className="text-[#FF4343]">Freddy Leads</span></h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 pr-4 text-[#6B7280] font-mono text-xs uppercase tracking-wider">Metric</th>
                  <th className="py-4 px-4 text-[#FF4343] font-mono text-xs uppercase tracking-wider bg-[#FF4343]/5">Freddy Leads</th>
                  <th className="py-4 px-4 text-[#6B7280] font-mono text-xs uppercase tracking-wider">LinkedIn Ads</th>
                  <th className="py-4 px-4 text-[#6B7280] font-mono text-xs uppercase tracking-wider">Trade Pubs</th>
                  <th className="py-4 px-4 text-[#6B7280] font-mono text-xs uppercase tracking-wider">Trade Shows</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Audience Intent',  'High',         'Medium',         'Low',            'High'],
                  ['Trust Level',      'Very High',    'Low',            'Medium',         'Medium'],
                  ['Content Auth.',    'Authentic',    'Brand-created',  'Editorial',      'Brand-created'],
                  ['Pricing Model',    'Per deliverable', 'Per impression', 'Per placement', 'Per booth'],
                ].map(([metric, ...vals], i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-3 pr-4 text-white font-medium">{metric}</td>
                    <td className="py-3 px-4 text-[#FF4343] font-mono font-semibold bg-[#FF4343]/5">{vals[0]}</td>
                    {vals.slice(1).map((v, j) => (
                      <td key={j} className="py-3 px-4 text-[#9CA3AF] font-mono">{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div><AnimatedCounter end={485} suffix="K" /><p className="text-[#6B7280] text-xs font-mono uppercase tracking-wider mt-2">Total Impressions</p></div>
            <div><AnimatedCounter end={25} prefix="$" /><p className="text-[#6B7280] text-xs font-mono uppercase tracking-wider mt-2">Avg CPM</p></div>
            <div><AnimatedCounter end={50} suffix="+" /><p className="text-[#6B7280] text-xs font-mono uppercase tracking-wider mt-2">Active Executives</p></div>
            <div><AnimatedCounter end={4} suffix=".2%" /><p className="text-[#6B7280] text-xs font-mono uppercase tracking-wider mt-2">Avg Engagement</p></div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      {caseStudies.length > 0 && (
        <section className="py-16 sm:py-20 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-12">Proven <span className="text-[#FF4343]">Results</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {caseStudies.map((cs, i) => (
                <div key={cs.id} className="bg-[#111827] border border-white/10 p-4 sm:p-8 border-l-[3px] border-l-[#FF4343]">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-mono uppercase tracking-wider px-2 py-1 bg-white/5 text-[#9CA3AF]">{cs.industry}</span>
                    <span className="text-xs font-mono uppercase tracking-wider text-[#6B7280]">{cs.campaign_type}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
                    <div className="min-w-0"><span className="block font-mono text-base sm:text-2xl font-bold text-[#FF4343] tabular-nums leading-tight truncate">${cs.total_spend?.toLocaleString()}</span><p className="text-[10px] sm:text-xs text-[#6B7280] mt-1">Total Spend</p></div>
                    <div className="min-w-0"><span className="block font-mono text-base sm:text-2xl font-bold text-white tabular-nums leading-tight truncate">{(cs.total_impressions / 1000).toFixed(0)}K</span><p className="text-[10px] sm:text-xs text-[#6B7280] mt-1">Impressions</p></div>
                    <div className="min-w-0"><span className="block font-mono text-base sm:text-2xl font-bold text-[#10B981] tabular-nums leading-tight truncate">${cs.cpm}</span><p className="text-[10px] sm:text-xs text-[#6B7280] mt-1">CPM</p></div>
                  </div>
                  <p className="text-sm text-[#9CA3AF] italic border-t border-white/10 pt-4">&ldquo;{cs.outcome_quote}&rdquo;</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/case-studies" data-testid="view-all-studies" className="text-[#FF4343] text-sm font-medium hover:underline inline-flex items-center gap-1">
                View All Case Studies <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Vertical CTAs */}
      <section className="py-16 sm:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center mb-12">Your <span className="text-[#FF4343]">Industry</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {verticals.map(v => (
              <Link key={v.key} to={`/deck/${v.key}`} data-testid={`deck-link-${v.key}`}
                className="bg-[#111827] border border-white/10 hover:border-[#FF4343]/30 p-4 sm:p-6 flex items-center justify-between transition-all duration-200 group">
                <span className="font-medium text-sm sm:text-base">{v.label}</span>
                <ChevronRight className="w-5 h-5 text-[#6B7280] group-hover:text-[#FF4343] transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Powered by Freddy Media */}
      <section className="py-8 sm:py-14 bg-white" data-testid="powered-by-banner">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-5">
              <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/lnrtssp0_freddy-logo-script.png" alt="Freddy Media" className="h-10 sm:h-14" />
              <div className="h-10 w-px bg-[#090E33]/10 hidden sm:block" />
              <div>
                <p className="text-[#090E33] text-sm font-semibold tracking-wide">Powered by Freddy Media</p>
                <p className="text-[#090E33]/50 text-xs mt-0.5">The Executive Endorsement Agency</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setFormOpen(true)} data-testid="powered-by-cta"
                className="border-2 border-[#090E33] bg-[#090E33] text-white hover:bg-[#090E33]/90 px-6 py-2.5 text-sm font-semibold transition-all duration-300">
                Request a Proposal
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded meeting modal — replaces the old intake form. */}
      <LaunchCampaignChoiceModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        source="for_brands_page"
      />
    </div>
  );
}
